import { useState } from "react"
import {
  isValidPostcode,
  getGeodesicDistance,
  getLocationFromPostcode,
  loadLocations,
} from "./utils"
import useGoogleMap from "./useGoogleMap"

import ImporterMapPin from "@assets/icons/marker.png"
import DistributorMapPin from "@assets/icons/marker1.png"
import POSMapPin from "@assets/icons/marker2.png"
import MapPin from "@assets/icons/mapPinBar.svg"
import MapPinYellow from "@assets/icons/mapPinBarYellow.svg"

const useMapResults = (storeGroup, googleMapRef, maxLocationsInBounds = 12) => {
  const [currentLocation, setCurrentLocation] = useState(null)
  const [postcodeError, setPostcodeError] = useState(false)
  const [mapResults, setMapResults] = useState([])
  const [loading, setLoading] = useState(false)

  const { map, gMaps } = useGoogleMap(googleMapRef)

  const mapPin = location => {
    switch (location?.acf.store_type) {
      case "Distributor":
        return DistributorMapPin
      case "Importer":
        return ImporterMapPin
      case "POS":
        return POSMapPin

      default:
        return MapPin
    }
  }

  const fetchLocationData = async postcode => {
    setMapResults([])

    // Fail fast if postcode is invalid
    // (either in formatting or from API check)
    // if (!isValidPostcode(postcode)) {
    //   setPostcodeError(true)
    //   return false
    // }

    let userLocation = null

    try {
      setPostcodeError(false)
      userLocation = await getLocationFromPostcode(postcode)

      setCurrentLocation(userLocation)
      if (!userLocation || !userLocation.lat || !userLocation.lon) {
        setPostcodeError(true)
        return false
      }
    } catch (error) {
      setPostcodeError(true)
      console.error(error)
      return false
    }

    try {
      // Clear any previous markers from map
      mapResults.forEach(location => location.marker.setMap(null))

      setLoading(true)

      const locations = await loadLocations(
        storeGroup?.databaseId,
        userLocation.lat,
        userLocation.lon,
      )

      setLoading(false)

      if (!locations || locations.length < 1) return false

      // Sort by distance
      let locationsSortedByDistance = []
      for (let i = 0; i < locations.length; i++) {
        const location = locations[i]

        const shopLocation = {
          lat: location.acf.location.lat,
          lng: location.acf.location.lng,
        }

        const distance = getGeodesicDistance(
          shopLocation.lat,
          shopLocation.lng,
          userLocation.lat,
          userLocation.lon,
        )

        if (distance !== "NaN") {
          locationsSortedByDistance.push({
            ...location,
            distance,
          })
        }
      }

      locationsSortedByDistance
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 10)
      locationsSortedByDistance = locationsSortedByDistance.slice(0, 10)

      // Add distance, marker and onClick properties
      // to each of the returned locations
      const augmentedLocations = locationsSortedByDistance.map(location => {
        const shopLocation = {
          lat: location.acf.location.lat,
          lng: location.acf.location.lng,
        }
        const position = {
          lat: parseFloat(shopLocation.lat),
          lng: parseFloat(shopLocation.lng),
        }

        const marker = new gMaps.Marker({
          position,
          icon: mapPin(location),
          map: map,
        })
        const onClick = () => {
          marker.setIcon(MapPinYellow)
          map.panTo(position)
          map.setZoom(16)
        }
        return {
          ...location,
          marker,
          onClick,
        }
      })

      setMapResults(augmentedLocations)

      // Pan the map to the postcode location
      // and ensure the boundaries include the first few results
      const center = new gMaps.LatLng(userLocation.lat, userLocation.lon)
      map.panTo(center)
      const bounds = new gMaps.LatLngBounds()

      bounds.extend(center)
      augmentedLocations
        .filter((_, index) => index < maxLocationsInBounds)
        .forEach(location => bounds.extend(location.marker.position))
      map.fitBounds(bounds)
    } catch (error) {
      console.error(error)

      setMapResults([])
      setLoading(false)
    }
  }

  const resetMarkers = () =>
    mapResults.forEach(location => location.marker.setIcon(mapPin(location)))

  return {
    currentLocation,
    mapResults,
    loading,
    postcodeError,
    fetchLocationData,
    resetMarkers,
  }
}

export default useMapResults
